import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { 
  User,
  BusinessOwner, 
  SystemStats, 
  PaymentProof, 
  OrderSummary,
  Campaign
} from '../models'

interface AdminAuthState {
  admin: User | null // Admin is now just a User with system_admin or monitor role
  token: string | null
  isAuthenticated: boolean
  isLoading: boolean
}

interface AdminDataState {
  // System overview
  systemStats: SystemStats | null
  
  // User management
  businessOwners: BusinessOwner[]
  businessOwnersLoading: boolean
  businessOwnersPagination: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
  
  // Payment management
  pendingPayments: PaymentProof[]
  pendingPaymentsLoading: boolean
  
  // Order management
  recentOrders: OrderSummary[]
  recentOrdersLoading: boolean
  
  // Campaign analytics
  campaigns: Campaign[]
  campaignsLoading: boolean
}

interface AdminActions {
  // Authentication
  setAuth: (admin: User, token: string) => void
  clearAuth: () => void
  setLoading: (loading: boolean) => void
  initializeAuth: () => void
  
  // System stats
  setSystemStats: (stats: SystemStats) => void
  
  // User management
  setBusinessOwners: (owners: BusinessOwner[], pagination: any) => void
  setBusinessOwnersLoading: (loading: boolean) => void
  updateBusinessOwner: (id: number, updates: Partial<BusinessOwner>) => void
  suspendBusinessOwner: (id: number) => void
  activateBusinessOwner: (id: number) => void
  
  // Payment management
  setPendingPayments: (payments: PaymentProof[]) => void
  setPendingPaymentsLoading: (loading: boolean) => void
  approvePayment: (paymentId: number) => void
  rejectPayment: (paymentId: number, reason: string) => void
  
  // Order management
  setRecentOrders: (orders: OrderSummary[]) => void
  setRecentOrdersLoading: (loading: boolean) => void
  
  // Campaign management
  setCampaigns: (campaigns: Campaign[]) => void
  setCampaignsLoading: (loading: boolean) => void
}

type AdminState = AdminAuthState & AdminDataState & AdminActions

export const useAdminStore = create<AdminState>()(
  persist(
    (set, get) => ({
      // Auth state
      admin: null,
      token: null,
      isAuthenticated: false,
      isLoading: true,
      
      // Data state
      systemStats: null,
      businessOwners: [],
      businessOwnersLoading: false,
      businessOwnersPagination: {
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0
      },
      pendingPayments: [],
      pendingPaymentsLoading: false,
      recentOrders: [],
      recentOrdersLoading: false,
      campaigns: [],
      campaignsLoading: false,

      // Auth actions
      setAuth: (admin: User, token: string) => {
        set({
          admin,
          token,
          isAuthenticated: true,
          isLoading: false,
        })
      },

      clearAuth: () => {
        set({
          admin: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
          // Clear all data when logging out
          systemStats: null,
          businessOwners: [],
          pendingPayments: [],
          recentOrders: [],
          campaigns: []
        })
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading })
      },

      initializeAuth: () => {
        const { token } = get()
        if (token) {
          set({ isAuthenticated: true, isLoading: false })
        } else {
          set({ isLoading: false })
        }
      },

      // System stats actions
      setSystemStats: (stats: SystemStats) => {
        set({ systemStats: stats })
      },

      // User management actions
      setBusinessOwners: (owners: BusinessOwner[], pagination: any) => {
        set({ 
          businessOwners: owners,
          businessOwnersPagination: pagination,
          businessOwnersLoading: false
        })
      },

      setBusinessOwnersLoading: (loading: boolean) => {
        set({ businessOwnersLoading: loading })
      },

      updateBusinessOwner: (id: number, updates: Partial<BusinessOwner>) => {
        set(state => ({
          businessOwners: state.businessOwners.map(owner =>
            owner.id === id ? { ...owner, ...updates } : owner
          )
        }))
      },

      suspendBusinessOwner: (id: number) => {
        // This would typically make an API call, then update the store
        set(state => ({
          businessOwners: state.businessOwners.map(owner =>
            owner.id === id ? { ...owner, isActive: false } : owner
          )
        }))
      },

      activateBusinessOwner: (id: number) => {
        set(state => ({
          businessOwners: state.businessOwners.map(owner =>
            owner.id === id ? { ...owner, isActive: true } : owner
          )
        }))
      },

      // Payment management actions
      setPendingPayments: (payments: PaymentProof[]) => {
        set({ 
          pendingPayments: payments,
          pendingPaymentsLoading: false
        })
      },

      setPendingPaymentsLoading: (loading: boolean) => {
        set({ pendingPaymentsLoading: loading })
      },

      approvePayment: (paymentId: number) => {
        set(state => ({
          pendingPayments: state.pendingPayments.filter(payment => payment.id !== paymentId)
        }))
      },

      rejectPayment: (paymentId: number, _reason: string) => {
        set(state => ({
          pendingPayments: state.pendingPayments.filter(payment => payment.id !== paymentId)
        }))
      },

      // Order management actions
      setRecentOrders: (orders: OrderSummary[]) => {
        set({ 
          recentOrders: orders,
          recentOrdersLoading: false
        })
      },

      setRecentOrdersLoading: (loading: boolean) => {
        set({ recentOrdersLoading: loading })
      },

      // Campaign management actions
      setCampaigns: (campaigns: Campaign[]) => {
        set({ 
          campaigns: campaigns,
          campaignsLoading: false
        })
      },

      setCampaignsLoading: (loading: boolean) => {
        set({ campaignsLoading: loading })
      },
    }),
    {
      name: 'admin-auth-storage',
      partialize: (state) => ({
        admin: state.admin,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
)